import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";
import { RequirePlan } from "../components/RouteGuard";
import { Bolt } from "@solar-icons/react";
import { Zap, DollarSign, BarChart3, Clock, RefreshCw } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/x402-usage")({
  component: () => (
    <RequirePlan required="starter" featureName="X402 Usage" icon={<Bolt size={64} weight="LineDuotone" />}>
      <X402Usage />
    </RequirePlan>
  ),
});

interface UsagePayment {
  endpoint: string;
  amount_usd: number;
  thing_id: string | null;
  created_at: string;
}

interface EndpointBreakdown {
  endpoint: string;
  calls: number;
  total_usd: number;
}

interface UsageData {
  payments: UsagePayment[];
  total_calls: number;
  total_usd: number;
  period_start: string;
  period_end: string | null;
  breakdown: EndpointBreakdown[];
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatEndpoint(ep: string): string {
  return ep.replace(/^\//, "").replace(/-/g, " ");
}

const ENDPOINT_COLORS: Record<string, string> = {
  "/assess": "bg-penta-mint",
  "/identify": "bg-penta-blue",
  "/valuate": "bg-penta-coral",
  "/search": "bg-penta-grey-medium",
};

function X402Usage() {
  const { currentTenant } = useTenant();
  const [period, setPeriod] = useState("30d");

  const { data, isLoading } = useQuery<UsageData>({
    queryKey: ["x402-usage", currentTenant?.id, period],
    queryFn: async () => {
      const response = await tenantFetch(`/api/v1/x402/usage?period=${period}`);
      if (!response.ok) throw new Error("Failed to fetch usage");
      return response.json();
    },
    enabled: !!currentTenant?.id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-penta-grey-medium" size={24} />
      </div>
    );
  }

  const usage = data;
  const avgCost =
    usage && usage.total_calls > 0 ? usage.total_usd / usage.total_calls : 0;

  // Group payments by day for timeline
  const dailyCounts: Record<string, number> = {};
  if (usage?.payments) {
    for (const p of usage.payments) {
      const day = p.created_at.slice(0, 10);
      dailyCounts[day] = (dailyCounts[day] || 0) + 1;
    }
  }
  const sortedDays = Object.entries(dailyCounts).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const maxDaily = Math.max(...sortedDays.map(([, c]) => c), 1);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h3 text-penta-black">x402 Usage</h1>
          <p className="text-body text-penta-grey-medium mt-1">
            Agent API calls via x402 payment protocol
          </p>
        </div>
        <div className="flex items-center gap-2">
          {["7d", "30d", "90d"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-sm font-mono uppercase tracking-wider ${
                period === p
                  ? "bg-penta-black text-white"
                  : "bg-penta-grey-light text-penta-grey-medium hover:text-penta-black"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-penta-grey-medium">Total Calls</span>
            <Zap className="text-penta-mint" size={20} />
          </div>
          <p className="text-2xl font-light text-penta-black">
            {usage?.total_calls?.toLocaleString() || 0}
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-penta-grey-medium">Total Spent</span>
            <DollarSign className="text-penta-blue" size={20} />
          </div>
          <p className="text-2xl font-light text-penta-black">
            {formatUsd(usage?.total_usd || 0)}
          </p>
          <p className="text-xs text-penta-grey-medium mt-1">USDC on Base</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-penta-grey-medium">
              Avg Cost/Call
            </span>
            <BarChart3 className="text-penta-coral" size={20} />
          </div>
          <p className="text-2xl font-light text-penta-black">
            {formatUsd(avgCost)}
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-penta-grey-medium">
              Last Activity
            </span>
            <Clock className="text-penta-grey-medium" size={20} />
          </div>
          <p className="text-2xl font-light text-penta-black">
            {usage?.period_end
              ? formatDateTime(usage.period_end)
              : "No activity"}
          </p>
        </div>
      </div>

      {/* Endpoint Breakdown + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Endpoint Breakdown */}
        <div className="card">
          <h2 className="text-body font-medium text-penta-black mb-4">
            By Endpoint
          </h2>
          {usage?.breakdown && usage.breakdown.length > 0 ? (
            <div className="space-y-3">
              {usage.breakdown.map((ep) => {
                const pct =
                  usage.total_calls > 0
                    ? (ep.calls / usage.total_calls) * 100
                    : 0;
                const color =
                  ENDPOINT_COLORS[ep.endpoint] || "bg-penta-grey-medium";
                return (
                  <div key={ep.endpoint}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-mono text-penta-black">
                        {formatEndpoint(ep.endpoint)}
                      </span>
                      <span className="text-sm text-penta-grey-medium">
                        {ep.calls} calls &middot; {formatUsd(ep.total_usd)}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-penta-grey-light">
                      <div
                        className={`h-full ${color}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center py-4 text-penta-grey-medium">
              No usage data
            </p>
          )}
        </div>

        {/* Daily Timeline */}
        <div className="lg:col-span-2 card">
          <h2 className="text-body font-medium text-penta-black mb-4">
            Daily Volume
          </h2>
          {sortedDays.length > 0 ? (
            <div className="flex items-end gap-1 h-32">
              {sortedDays.map(([day, count]) => (
                <div
                  key={day}
                  className="flex-1 bg-penta-mint hover:bg-penta-mint/80 transition-colors relative group"
                  style={{
                    height: `${(count / maxDaily) * 100}%`,
                    minHeight: "2px",
                  }}
                  title={`${day}: ${count} calls`}
                >
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-penta-black text-white text-xs px-2 py-1 whitespace-nowrap">
                    {day}: {count}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-penta-grey-medium">
              No daily data yet
            </div>
          )}
          {sortedDays.length > 0 && (
            <div className="flex justify-between mt-2">
              <span className="text-xs text-penta-grey-medium">
                {sortedDays[0]?.[0]}
              </span>
              <span className="text-xs text-penta-grey-medium">
                {sortedDays[sortedDays.length - 1]?.[0]}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Recent Calls Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-body font-medium text-penta-black">
            Recent Calls
          </h2>
          <span className="text-xs text-penta-grey-medium">
            Showing {usage?.payments?.length || 0} of {usage?.total_calls || 0}
          </span>
        </div>

        {usage?.payments && usage.payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-penta-grey-light">
                  <th className="text-left py-2 px-3 text-penta-grey-medium font-medium">
                    Endpoint
                  </th>
                  <th className="text-left py-2 px-3 text-penta-grey-medium font-medium">
                    Amount
                  </th>
                  <th className="text-left py-2 px-3 text-penta-grey-medium font-medium">
                    Thing ID
                  </th>
                  <th className="text-left py-2 px-3 text-penta-grey-medium font-medium">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {usage.payments.slice(0, 25).map((payment, i) => (
                  <tr
                    key={`${payment.created_at}-${i}`}
                    className="border-b border-penta-grey-light/50 hover:bg-penta-grey-light/30"
                  >
                    <td className="py-2 px-3">
                      <span className="font-mono text-penta-black">
                        {formatEndpoint(payment.endpoint)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-penta-black">
                      {formatUsd(payment.amount_usd)}
                    </td>
                    <td className="py-2 px-3">
                      {payment.thing_id ? (
                        <span className="font-mono text-xs text-penta-grey-medium">
                          {payment.thing_id.slice(0, 16)}...
                        </span>
                      ) : (
                        <span className="text-penta-grey-medium">-</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-penta-grey-medium">
                      {formatDateTime(payment.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-penta-grey-medium">
            <Zap className="mx-auto mb-2" size={32} strokeWidth={1} />
            <p>No x402 calls yet</p>
            <p className="text-xs mt-1">
              Brand agents will appear here when they start using the x402 API
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
